import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, Lightbulb, Eye } from 'lucide-react';

interface TarotCardDetailProps {
  card: any;
  onClose: () => void;
  onMastered?: () => void;
}

export default function TarotCardDetail({ card, onClose, onMastered }: TarotCardDetailProps) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Tarot Card Details</DialogTitle>
          <DialogDescription>Detailed information about the tarot card</DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[90vh]">
          <div className="p-6 space-y-6">
            {/* Header */}
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div>
                  <DialogTitle className="text-3xl mb-2 text-primary">{card.card_name || card.name}</DialogTitle>
                  <div className="flex gap-2">
                    <Badge className="bg-primary text-primary-foreground">{card.suit}</Badge>
                    <Badge className="bg-accent text-accent-foreground">Card {card.card_number || card.number}</Badge>
                  </div>
                </div>
              </div>
            </DialogHeader>

            {/* Card Image - Prominent Display */}
            <div className="relative aspect-[2/3] w-full max-w-sm mx-auto rounded-xl overflow-hidden shadow-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center">
              {(card.image_url || card.imageUrl) ? (
                <>
                  <img 
                    src={card.image_url || card.imageUrl} 
                    alt={card.card_name || card.name}
                    className="w-full h-full object-contain p-6"
                    crossOrigin="anonymous"
                    onError={(e) => {
                      console.error('Failed to load tarot card image:', card.image_url || card.imageUrl, card);
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) (fallback as HTMLElement).style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full hidden items-center justify-center p-6 bg-gradient-to-br from-primary/10 to-accent/10 absolute inset-0">
                    <div className="text-center">
                      <Eye className="h-16 w-16 mx-auto mb-4 text-primary" />
                      <p className="text-sm font-medium text-primary">
                        Image unavailable
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {card.card_name || card.name}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center p-6 bg-gradient-to-br from-primary/10 to-accent/10">
                  <div className="text-center">
                    <Eye className="h-16 w-16 mx-auto mb-4 text-primary" />
                    <p className="text-sm font-medium text-primary">
                      Upload Rider-Waite card image
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      The imagery tells the story
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Keywords */}
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2 text-primary">
                <Lightbulb className="h-4 w-4" />
                Keywords
              </h4>
              <div className="flex flex-wrap gap-2">
                {(card.keywords || []).map((keyword: string, idx: number) => (
                  <Badge key={idx} className="bg-primary/10 text-primary hover:bg-primary/20">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* The Story */}
            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2 text-primary">
                <BookOpen className="h-4 w-4" />
                The Story
              </h4>
              <p className="text-sm leading-relaxed">
                {card.story}
              </p>
            </div>

            <Separator />

            {/* Imagery Explanation */}
            {card.imagery_explanation && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2 text-accent">
                    <Eye className="h-4 w-4" />
                    Reading the Imagery
                  </h4>
                  <p className="text-sm leading-relaxed bg-accent/10 p-4 rounded-lg border border-accent/20">
                    {card.imagery_explanation}
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    Understanding the visual symbols helps you remember and intuitively interpret this card
                  </p>
                </div>
              </>
            )}

            <Separator />

            {/* Meanings */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 p-4 rounded-lg bg-primary/5 border border-primary/20">
                <h4 className="font-semibold text-primary">
                  Upright Meaning
                </h4>
                <p className="text-sm">{card.upright_meaning || card.uprightMeaning}</p>
              </div>
              <div className="space-y-2 p-4 rounded-lg bg-accent/5 border border-accent/20">
                <h4 className="font-semibold text-accent">
                  Reversed Meaning
                </h4>
                <p className="text-sm">{card.reversed_meaning || card.reversedMeaning}</p>
              </div>
            </div>

            {/* Symbolism */}
            {card.symbolism && card.symbolism.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="font-semibold text-primary">Symbolic Elements</h4>
                  <ul className="space-y-2">
                    {card.symbolism.map((symbol: string, idx: number) => (
                      <li key={idx} className="text-sm flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{symbol}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* Journal Prompts */}
            {(card.journal_prompts || card.journalPrompts) && (card.journal_prompts || card.journalPrompts).length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="font-semibold text-primary">Reflection Prompts</h4>
                  <div className="space-y-3">
                    {(card.journal_prompts || card.journalPrompts).map((prompt: string, idx: number) => (
                      <div key={idx} className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                        <p className="text-sm italic">{prompt}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={onClose}
              >
                Close
              </Button>
              {onMastered && (
                <Button 
                  className="flex-1 bg-gradient-to-r from-primary to-accent hover:from-primary-light hover:to-accent text-background"
                  onClick={() => {
                    onMastered();
                    onClose();
                  }}
                >
                  Mark as Studied
                </Button>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
